import { Action } from 'redux';
import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import { ProcessConfigDefaultRun } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types/RcInstanceDB';
import { ThunkAction } from 'redux-thunk';
import { ApiResponse } from 'apisauce';
import { processService } from '../services/processAPI';
import { processStartService } from '../services/processStartAPI';
import { OkAction, ErrorAction, Editing } from './baseActions';
import { ProcessStatus } from '../types/resources';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

// To avoid circular imports between modules, we must import only the AppState type here
type AppState = import('~/store').AppState;

export enum ProcessActionType {
  SILENT_GET_PROCESSES = 'SILENT_GET_PROCESSES',
  GET_PROCESSES = 'GET_PROCESSES',
  GET_PROCESSES_OK = 'GET_PROCESSES_OK',
  GET_PROCESSES_ERROR = 'GET_PROCESSES_ERROR',
  SILENT_GET_PROCESS_STATUS = 'SILENT_GET_PROCESS_STATUS',
  GET_PROCESS_STATUS = 'GET_PROCESS_STATUS',
  GET_PROCESS_STATUS_OK = 'GET_PROCESS_STATUS_OK',
  GET_PROCESS_STATUS_ERROR = 'GET_PROCESS_STATUS_ERROR',
  SILENT_LIST_PROCESS_STATUSES = 'SILENT_LIST_PROCESS_STATUSES',
  LIST_PROCESS_STATUSES = 'LIST_PROCESS_STATUSES',
  LIST_PROCESS_STATUSES_OK = 'LIST_PROCESS_STATUSES_OK',
  LIST_PROCESS_STATUSES_ERROR = 'LIST_PROCESS_STATUSES_ERROR',
  RESET_PROCESS_STATUS = 'RESET_PROCESS_STATUS',
  RESET_PROCESS_STATUS_OK = 'RESET_PROCESS_STATUS_OK',
  RESET_PROCESS_STATUS_ERROR = 'RESET_PROCESS_STATUS_ERROR',
  SILENT_GET_PROCESS = 'SILENT_GET_PROCESS',
  GET_PROCESS = 'GET_PROCESS',
  GET_PROCESS_OK = 'GET_PROCESS_OK',
  GET_PROCESS_ERROR = 'GET_PROCESS_ERROR',
  EDIT_PROCESS = 'EDIT_PROCESS',
  EDIT_PROCESS_OK = 'EDIT_PROCESS_OK',
  EDIT_PROCESS_ERROR = 'EDIT_PROCESS_ERROR',
  DELETE_PROCESS = 'DELETE_PROCESS',
  DELETE_PROCESS_OK = 'DELETE_PROCESS_OK',
  DELETE_PROCESS_ERROR = 'DELETE_PROCESS_ERROR',
  CREATE_PROCESS = 'CREATE_PROCESS',
  CREATE_PROCESS_OK = 'CREATE_PROCESS_OK',
  CREATE_PROCESS_ERROR = 'CREATE_PROCESS_ERROR',
  GENERATE_PROCESS_SECRET = 'GENERATE_PROCESS_SECRET',
  GENERATE_PROCESS_SECRET_OK = 'GENERATE_PROCESS_SECRET_OK',
  GENERATE_PROCESS_SECRET_ERROR = 'GENERATE_PROCESS_SECRET_ERROR',
  DELETE_PROCESS_SECRET = 'DELETE_PROCESS_SECRET',
  DELETE_PROCESS_SECRET_OK = 'DELETE_PROCESS_SECRET_OK',
  DELETE_PROCESS_SECRET_ERROR = 'DELETE_PROCESS_SECRET_ERROR',
  UPDATE_PROCESS_ACTIVITY_ORDER = 'UPDATE_PROCESS_ACTIVITY_ORDER',
  UPDATE_PROCESS_ACTIVITY_ORDER_OK = 'UPDATE_PROCESS_ACTIVITY_ORDER_OK',
  UPDATE_PROCESS_ACTIVITY_ORDER_ERROR = 'UPDATE_PROCESS_ACTIVITY_ORDER_ERROR',
}

export type ProcessEditingDetails =
  | Editing<'changingName', 'changingNameError'>
  | Editing<'changingTimeout', 'changingTimeoutError'>
  | Editing<'changingDefaultRun', 'changingDefaultRunError'>
  | Editing<'changingExplicitOutput', 'changingExplicitOutputError'>
  | Editing<'changingEmailNotificationConfig', 'editEmailNotificationConfigError'>;

type WithProcessId = { processId: string };

type GetProcessesAction =
  | Action<ProcessActionType.SILENT_GET_PROCESSES>
  | Action<ProcessActionType.GET_PROCESSES>
  | OkAction<ProcessActionType.GET_PROCESSES_OK, RcInstanceDTO.Process[]>
  | ErrorAction<ProcessActionType.GET_PROCESSES_ERROR>;

type GetProcessStatus =
  | Action<ProcessActionType.SILENT_GET_PROCESS_STATUS>
  | (Action<ProcessActionType.GET_PROCESS_STATUS> & WithProcessId)
  | (OkAction<ProcessActionType.GET_PROCESS_STATUS_OK, ProcessStatus> & WithProcessId)
  | (ErrorAction<ProcessActionType.GET_PROCESS_STATUS_ERROR> & WithProcessId);

type ResetProcessStatus =
  | (Action<ProcessActionType.RESET_PROCESS_STATUS> & WithProcessId)
  | (OkAction<ProcessActionType.RESET_PROCESS_STATUS_OK, ProcessStatus> & WithProcessId)
  | (ErrorAction<ProcessActionType.RESET_PROCESS_STATUS_ERROR> & WithProcessId);

type ListProcessStatuses =
  | Action<ProcessActionType.SILENT_LIST_PROCESS_STATUSES>
  | Action<ProcessActionType.LIST_PROCESS_STATUSES>
  | OkAction<ProcessActionType.LIST_PROCESS_STATUSES_OK, ProcessStatus[]>
  | ErrorAction<ProcessActionType.LIST_PROCESS_STATUSES_ERROR>;

type GetProcessByIdAction =
  | Action<ProcessActionType.SILENT_GET_PROCESS>
  | (Action<ProcessActionType.GET_PROCESS> & WithProcessId)
  | (OkAction<ProcessActionType.GET_PROCESS_OK, RcInstanceDTO.Process> & WithProcessId)
  | (ErrorAction<ProcessActionType.GET_PROCESS_ERROR> & WithProcessId);

type EditProcessByIdAction =
  | (Action<ProcessActionType.EDIT_PROCESS> & WithProcessId & ProcessEditingDetails)
  | (OkAction<ProcessActionType.EDIT_PROCESS_OK, RcInstanceDTO.Process> &
      WithProcessId &
      ProcessEditingDetails)
  | (ErrorAction<ProcessActionType.EDIT_PROCESS_ERROR> & WithProcessId & ProcessEditingDetails);

type DeleteProcessByIdAction =
  | (Action<ProcessActionType.DELETE_PROCESS> & WithProcessId)
  | (OkAction<ProcessActionType.DELETE_PROCESS_OK, RcInstanceDTO.Process> & WithProcessId)
  | (ErrorAction<ProcessActionType.DELETE_PROCESS_ERROR> & WithProcessId);

type CreateProcessAction =
  | Action<ProcessActionType.CREATE_PROCESS>
  | OkAction<ProcessActionType.CREATE_PROCESS_OK, RcInstanceDTO.Process>
  | ErrorAction<ProcessActionType.CREATE_PROCESS_ERROR>;

type GenerateProcessSecretByIdAction =
  | (Action<ProcessActionType.GENERATE_PROCESS_SECRET> & WithProcessId)
  | (OkAction<ProcessActionType.GENERATE_PROCESS_SECRET_OK, RcInstanceDTO.Process> & WithProcessId)
  | (ErrorAction<ProcessActionType.GENERATE_PROCESS_SECRET_ERROR> & WithProcessId);

type DeleteProcessSecretByIdAction =
  | (Action<ProcessActionType.DELETE_PROCESS_SECRET> & WithProcessId)
  | (Action<ProcessActionType.DELETE_PROCESS_SECRET_OK> & WithProcessId)
  | (ErrorAction<ProcessActionType.DELETE_PROCESS_SECRET_ERROR> & WithProcessId);

export type UpdateProcessActivityOrderAction =
  | (OkAction<ProcessActionType.UPDATE_PROCESS_ACTIVITY_ORDER, string[]> & WithProcessId)
  | (OkAction<ProcessActionType.UPDATE_PROCESS_ACTIVITY_ORDER_OK, RcInstanceDTO.Activity[]> &
      WithProcessId)
  | (ErrorAction<ProcessActionType.UPDATE_PROCESS_ACTIVITY_ORDER_ERROR> & WithProcessId);

export type ProcessAction =
  | GetProcessesAction
  | GetProcessByIdAction
  | EditProcessByIdAction
  | DeleteProcessByIdAction
  | CreateProcessAction
  | GenerateProcessSecretByIdAction
  | ListProcessStatuses
  | GetProcessStatus
  | ResetProcessStatus
  | DeleteProcessSecretByIdAction;

export const getProcessStatus =
  (processId: string, showError = true): ThunkAction<void, AppState, null, GetProcessStatus> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.processStatuses.byId[processId]) {
      dispatch({ type: ProcessActionType.SILENT_GET_PROCESS_STATUS });
    } else {
      dispatch({ type: ProcessActionType.GET_PROCESS_STATUS, processId });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processStartService.getProcessStatus({ workspaceId, processId });

    if (response.ok) {
      dispatch({
        processId,
        type: ProcessActionType.GET_PROCESS_STATUS_OK,
        data: response.data as ProcessStatus,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.GET_PROCESS_STATUS_ERROR,
        processId,
        showSnackbar: showError,
      });
    }
  };

export const resetProcessStatus =
  (processId: string): ThunkAction<void, AppState, null, ResetProcessStatus> =>
  async (dispatch, getState) => {
    dispatch({ type: ProcessActionType.RESET_PROCESS_STATUS, processId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processStartService.resetStatus({ workspaceId, processId });

    if (response.ok) {
      dispatch({
        processId,
        type: ProcessActionType.RESET_PROCESS_STATUS_OK,
        data: response.data as ProcessStatus,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.RESET_PROCESS_STATUS_ERROR,
        processId,
      });
    }
  };

export const listProcessStatuses =
  (): ThunkAction<void, AppState, null, ListProcessStatuses> => async (dispatch, getState) => {
    if (getState().workspaceResources.processStatuses.allIds.length > 1) {
      dispatch({ type: ProcessActionType.SILENT_LIST_PROCESS_STATUSES });
    } else {
      dispatch({ type: ProcessActionType.LIST_PROCESS_STATUSES });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processStartService.listWorkspaceProcessStatuses({ workspaceId });

    if (response.ok) {
      dispatch({
        type: ProcessActionType.LIST_PROCESS_STATUSES_OK,
        data: response.data?.data as ProcessStatus[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.LIST_PROCESS_STATUSES_ERROR,
      });
    }
  };

export const getProcesses =
  (): ThunkAction<void, AppState, null, GetProcessesAction> => async (dispatch, getState) => {
    if (getState().workspaceResources.processes.allIds.length > 1) {
      dispatch({ type: ProcessActionType.SILENT_GET_PROCESSES });
    } else {
      dispatch({ type: ProcessActionType.GET_PROCESSES });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processService.getAll({ workspaceId });
    if (response.ok) {
      dispatch({
        type: ProcessActionType.GET_PROCESSES_OK,
        data: response.data as RcInstanceDTO.Process[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.GET_PROCESSES_ERROR,
      });
    }
  };

export const getProcessById =
  (processId: string): ThunkAction<void, AppState, null, GetProcessByIdAction> =>
  async (dispatch, getState) => {
    if (getState().workspaceResources.processes.byId[processId]) {
      dispatch({ type: ProcessActionType.SILENT_GET_PROCESS });
    } else {
      dispatch({ type: ProcessActionType.GET_PROCESS, processId });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processService.getProcessById({ workspaceId, processId });
    if (response.ok) {
      dispatch({
        processId,
        type: ProcessActionType.GET_PROCESS_OK,
        data: response.data as RcInstanceDTO.Process,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.GET_PROCESS_ERROR,
        processId,
      });
    }
  };

const editProcess =
  (
    processId: string,
    update: Partial<RcInstanceDTO.Process>,
    editingDetails: ProcessEditingDetails,
  ): ThunkAction<void, AppState, null, EditProcessByIdAction> =>
  async (dispatch, getState) => {
    const process = getState().workspaceResources.processes.byId[processId];
    const updated = { ...process, ...update };

    if (typeof update.config === 'object') {
      updated.config = { ...process.config, ...update.config };
    }

    dispatch({
      type: ProcessActionType.EDIT_PROCESS,
      processId,
      ...editingDetails,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processService.editProcessById({
      workspaceId,
      processId,
      process: updated,
    });
    if (response.ok) {
      dispatch({
        processId,
        ...editingDetails,
        type: ProcessActionType.EDIT_PROCESS_OK,
        data: response.data as RcInstanceDTO.Process,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        ...editingDetails,
        type: ProcessActionType.EDIT_PROCESS_ERROR,
        processId,
      });
    }
  };

export const deleteProcessById =
  (
    processId: string,
  ): ThunkAction<
    Promise<ApiResponse<RcInstanceDTO.Process>>,
    AppState,
    null,
    DeleteProcessByIdAction
  > =>
  async (dispatch, getState) => {
    dispatch({ type: ProcessActionType.DELETE_PROCESS, processId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processService.deleteOneById({ workspaceId, id: processId });
    if (response.ok) {
      dispatch({
        processId,
        type: ProcessActionType.DELETE_PROCESS_OK,
        data: response.data as RcInstanceDTO.Process,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.DELETE_PROCESS_ERROR,
        processId,
      });
    }

    return response;
  };

export const createProcess =
  (
    name: string,
    explicitOutput: boolean,
    description: string | null,
  ): ThunkAction<
    Promise<ApiResponse<RcInstanceDTO.Process>>,
    AppState,
    null,
    CreateProcessAction
  > =>
  async (dispatch, getState) => {
    dispatch({ type: ProcessActionType.CREATE_PROCESS });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processService.create({
      workspaceId,
      name,
      explicitOutput,
      description,
    });
    if (response.ok) {
      dispatch({
        type: ProcessActionType.CREATE_PROCESS_OK,
        data: response.data as RcInstanceDTO.Process,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.CREATE_PROCESS_ERROR,
      });
    }

    return response;
  };

export const changeProcessTimeout = (processId: string, executionTimeout: number) =>
  editProcess(
    processId,
    { config: { executionTimeout } },
    { statusKey: 'changingTimeout', errorKey: 'changingTimeoutError' },
  );

export const changeExplicitOutput = (processId: string, explicitOutput: boolean) =>
  editProcess(
    processId,
    { config: { explicitOutput } },
    { statusKey: 'changingExplicitOutput', errorKey: 'changingExplicitOutputError' },
  );

export const changeProcessName = (processId: string, name: string) =>
  editProcess(processId, { name }, { statusKey: 'changingName', errorKey: 'changingNameError' });

export const changeDefaultRun = (
  processId: string,
  defaultRun: ProcessConfigDefaultRun | undefined,
) =>
  editProcess(
    processId,
    { config: { defaultRun } },
    { statusKey: 'changingDefaultRun', errorKey: 'changingDefaultRunError' },
  );

export const addFailedRunEmail =
  (processId: string, email: string): ThunkAction<void, AppState, null, EditProcessByIdAction> =>
  async (dispatch, getState) => {
    const process = getState().workspaceResources.processes.byId[processId];
    const emailNotifications =
      process.config.emailNotifications || ({} as RcInstanceDTO.ProcessConfigEmailNotifications);
    let failedRuns: string[] = [];
    if (emailNotifications.failedRuns) {
      failedRuns = [...emailNotifications.failedRuns];
    }
    failedRuns.push(email);

    dispatch(
      editProcess(
        processId,
        {
          config: {
            emailNotifications: { failedRuns, successfulRuns: emailNotifications.successfulRuns },
          },
        },
        {
          statusKey: 'changingEmailNotificationConfig',
          errorKey: 'editEmailNotificationConfigError',
        },
      ),
    );
  };

export const removeFailedRunEmail =
  (processId: string, email: string): ThunkAction<void, AppState, null, EditProcessByIdAction> =>
  async (dispatch, getState) => {
    const process = getState().workspaceResources.processes.byId[processId];
    const emailNotifications =
      process.config.emailNotifications || ({} as RcInstanceDTO.ProcessConfigEmailNotifications);
    let failedRuns: string[] = [];
    if (emailNotifications.failedRuns) {
      failedRuns = [...emailNotifications.failedRuns];
    }
    const filtered = failedRuns.filter((failedRunEmail) => failedRunEmail !== email);

    dispatch(
      editProcess(
        processId,
        {
          config: {
            emailNotifications: {
              failedRuns: filtered,
              successfulRuns: emailNotifications.successfulRuns,
            },
          },
        },
        {
          statusKey: 'changingEmailNotificationConfig',
          errorKey: 'editEmailNotificationConfigError',
        },
      ),
    );
  };

export const addSuccessfulRunEmail =
  (processId: string, email: string): ThunkAction<void, AppState, null, EditProcessByIdAction> =>
  async (dispatch, getState) => {
    const process = getState().workspaceResources.processes.byId[processId];
    const emailNotifications =
      process.config.emailNotifications ?? ({} as RcInstanceDTO.ProcessConfigEmailNotifications);

    const existingEmailsForSuccessfulRuns = emailNotifications.successfulRuns ?? [];
    const updatedEmailsForSuccessfulRuns = [...existingEmailsForSuccessfulRuns, email];

    const failedRuns = emailNotifications.failedRuns ?? [];

    dispatch(
      editProcess(
        processId,
        {
          config: {
            emailNotifications: { successfulRuns: updatedEmailsForSuccessfulRuns, failedRuns },
          },
        },
        {
          statusKey: 'changingEmailNotificationConfig',
          errorKey: 'editEmailNotificationConfigError',
        },
      ),
    );
  };

export const removeSuccessfulRunEmail =
  (processId: string, email: string): ThunkAction<void, AppState, null, EditProcessByIdAction> =>
  async (dispatch, getState) => {
    const process = getState().workspaceResources.processes.byId[processId];
    const emailNotifications =
      process.config.emailNotifications || ({} as RcInstanceDTO.ProcessConfigEmailNotifications);

    const existingEmailsForSuccessfulRuns: string[] = emailNotifications.successfulRuns ?? [];
    const updatedEmailsForSuccessfulRuns = existingEmailsForSuccessfulRuns.filter(
      (successfulRunEmail) => successfulRunEmail !== email,
    );

    const failedRuns = emailNotifications.failedRuns ?? [];

    dispatch(
      editProcess(
        processId,
        {
          config: {
            emailNotifications: { successfulRuns: updatedEmailsForSuccessfulRuns, failedRuns },
          },
        },
        {
          statusKey: 'changingEmailNotificationConfig',
          errorKey: 'editEmailNotificationConfigError',
        },
      ),
    );
  };

export const generateProcessSecret =
  (processId: string): ThunkAction<void, AppState, null, GenerateProcessSecretByIdAction> =>
  async (dispatch, getState) => {
    dispatch({ type: ProcessActionType.GENERATE_PROCESS_SECRET, processId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processService.generateProcessSecret({ workspaceId, processId });
    if (response.ok) {
      dispatch({
        processId,
        type: ProcessActionType.GENERATE_PROCESS_SECRET_OK,
        data: response.data as RcInstanceDTO.Process,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.GENERATE_PROCESS_SECRET_ERROR,
        processId,
      });
    }
  };

export const deleteProcessSecret =
  (processId: string): ThunkAction<void, AppState, null, DeleteProcessSecretByIdAction> =>
  async (dispatch, getState) => {
    dispatch({ type: ProcessActionType.DELETE_PROCESS_SECRET, processId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processService.deleteProcessSecret({ workspaceId, processId });
    if (response.ok) {
      dispatch({
        processId,
        type: ProcessActionType.DELETE_PROCESS_SECRET_OK,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.DELETE_PROCESS_SECRET_ERROR,
        processId,
      });
    }
  };

export const updateProcessActivityOrder =
  (
    processId: string,
    oldIndex: number,
    newIndex: number,
  ): ThunkAction<void, AppState, null, UpdateProcessActivityOrderAction> =>
  async (dispatch, getState) => {
    const data = [...getState().workspaceResources.activities.allIds];
    data.splice(newIndex, 0, data.splice(oldIndex, 1)[0]);

    const order: Record<number, string> = {};

    data.forEach((id, index) => {
      const orderNumber = index + 1;
      order[orderNumber] = id;
    });

    dispatch({
      type: ProcessActionType.UPDATE_PROCESS_ACTIVITY_ORDER,
      processId,
      data,
    });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await processService.updateProcessActivityOrder({
      workspaceId,
      processId,
      order,
    });
    if (response.ok && response.data) {
      dispatch({
        processId,
        type: ProcessActionType.UPDATE_PROCESS_ACTIVITY_ORDER_OK,
        data: response.data,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ProcessActionType.UPDATE_PROCESS_ACTIVITY_ORDER_ERROR,
        processId,
      });
    }
  };
